import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchEmployeesBatchForOrgChart, TimeoutError, withTimeout } from 'src/api/baseball-card-service';
import { countAllReportsUnderLeader, fetchAllReportsRecursively, fetchDirectReports, getEmployeeByLogin } from 'src/api/papi-service';
import { logger } from 'src/logger';
import { OrgChartEmployee } from '../types/orgChart.types';
import { Employee } from '../../ea-huddle-v2/interfaces/interface';
import { convertEmployeeToOrgChartEmployee, convertLoginsToEmails } from '../utils/employeeTypeAdapters';
import { transformToOrgChartEmployee } from '../utils/employeeTransformers';
import { collectSubtreeLogins } from '../utils/orgChartSelection';
import { API_TIMEOUT_MS, MAX_EMPLOYEES, MAX_EMPLOYEES_LIMIT_MESSAGE } from '../constants/BaseballCardConstants';

/**
 * Returns true if an employee has a usable job level. Employees without one are
 * hidden from the tree and excluded from card generation (they can't be scored).
 */
const hasValidJobLevel = (employee: OrgChartEmployee): boolean =>
  employee.jobLevel !== null && employee.jobLevel !== undefined && employee.jobLevel.trim() !== '';

interface UseOrgChartDataArgs {
  /** The alias the org chart is initially rooted at (typically the signed-in user). */
  initialLeaderAlias: string;
}

interface UseOrgChartDataReturn {
  currentLeaderAlias: string;
  currentLeaderData: OrgChartEmployee | null;
  viewMode: 'direct' | 'all';
  employees: Map<string, OrgChartEmployee>;
  filteredEmployeesForDisplay: Map<string, OrgChartEmployee>;
  expandedNodes: Set<string>;
  selectedEmployees: Set<string>;
  selectedEmployeeCount: number;
  activeEmployeeCount: number;
  existingEmails: Set<string>;
  isLoading: boolean;
  loadingProgress: number;
  totalEmployees: number;
  errorMessage: string | null;
  setErrorMessage: (message: string | null) => void;
  handleExpand: (login: string) => Promise<void>;
  handleCollapse: (login: string) => void;
  handleToggleSelection: (login: string) => void;
  handleAddEmployee: (eaHuddleEmployee: Employee) => void;
  handleViewModeChange: (mode: 'direct' | 'all') => void;
  handleLeaderChange: (eaHuddleEmployee: Employee) => void;
}

/**
 * Owns the org-chart data pipeline for the Baseball Cards flow: which leader is rooted,
 * the Direct/All view mode, the loaded employee map, expansion/selection state, and the
 * async load effect (with an AbortController that cancels in-flight requests when the
 * leader or view mode changes). Extracted from the page so the component stays
 * presentation-only.
 *
 * Selection uses an opt-OUT model: every loaded employee is selected by default, and
 * toggling adds/removes them from an internal `deselectedEmployees` set. The leader has
 * no checkbox and is always included when it has a valid job level.
 */
export const useOrgChartData = ({ initialLeaderAlias }: UseOrgChartDataArgs): UseOrgChartDataReturn => {
  const [currentLeaderAlias, setCurrentLeaderAlias] = useState<string>(initialLeaderAlias);
  const [currentLeaderData, setCurrentLeaderData] = useState<OrgChartEmployee | null>(null);
  const [employees, setEmployees] = useState<Map<string, OrgChartEmployee>>(new Map());
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [manuallyAddedEmployees, setManuallyAddedEmployees] = useState<Map<string, OrgChartEmployee>>(new Map());
  // Employees the user has explicitly opted OUT of; absence means "included".
  const [deselectedEmployees, setDeselectedEmployees] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<'direct' | 'all'>('direct');
  const [isLoading, setIsLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [totalEmployees, setTotalEmployees] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const activeEmployeeCount = useMemo(() => {
    let count = 0;
    employees.forEach((employee) => {
      if (hasValidJobLevel(employee)) count++;
    });
    return count;
  }, [employees]);

  // Positive set of selected logins (everyone minus those explicitly deselected).
  const selectedEmployees = useMemo(() => {
    const selected = new Set<string>();
    employees.forEach((_employee, login) => {
      if (!deselectedEmployees.has(login)) selected.add(login);
    });
    return selected;
  }, [employees, deselectedEmployees]);

  // Employees that will actually get a card: valid job level and not deselected.
  const selectedEmployeeCount = useMemo(() => {
    let count = 0;
    employees.forEach((employee, login) => {
      if (hasValidJobLevel(employee) && !deselectedEmployees.has(login)) count++;
    });
    return count;
  }, [employees, deselectedEmployees]);

  const existingLogins = useMemo(
    () => new Set([...Array.from(employees.keys()), ...Array.from(manuallyAddedEmployees.keys())]),
    [employees, manuallyAddedEmployees]
  );

  const existingEmails = useMemo(() => convertLoginsToEmails(existingLogins, employees), [existingLogins, employees]);

  // Only show employees with a valid job level (plus the leader and manual adds).
  const filteredEmployeesForDisplay = useMemo(() => {
    const filtered = new Map<string, OrgChartEmployee>();
    employees.forEach((employee, login) => {
      if (login === currentLeaderAlias || hasValidJobLevel(employee) || employee.isManuallyAdded) {
        filtered.set(login, employee);
      }
    });
    return filtered;
  }, [employees, currentLeaderAlias]);

  const handleExpand = useCallback(
    async (login: string) => {
      setExpandedNodes((prev) => new Set(prev).add(login));

      const employee = employees.get(login);
      if (employee && employee.directReports.length > 0) {
        const missingReports = employee.directReports.filter((reportLogin) => !employees.has(reportLogin) && reportLogin !== currentLeaderAlias);

        if (missingReports.length > 0) {
          try {
            const fetchedEmployees = await fetchEmployeesBatchForOrgChart(missingReports);
            setEmployees((prev) => {
              const newMap = new Map(prev);
              fetchedEmployees.forEach((emp, empLogin) => {
                if (!newMap.has(empLogin)) newMap.set(empLogin, emp);
              });
              return newMap;
            });
          } catch (error) {
            logger.error('Failed to fetch direct reports', {
              login,
              missingReports,
              error: error instanceof Error ? error.message : String(error)
            });
          }
        }
      }
    },
    [employees, currentLeaderAlias]
  );

  const handleCollapse = useCallback((login: string) => {
    setExpandedNodes((prev) => {
      const newSet = new Set(prev);
      newSet.delete(login);
      return newSet;
    });
  }, []);

  const handleToggleSelection = useCallback(
    (login: string) => {
      // A collapsed node acts on its whole visible subtree; an expanded node acts on just
      // that person (so you can fine-tune individuals once the branch is open).
      const targets = expandedNodes.has(login) ? new Set([login]) : collectSubtreeLogins(login, employees);
      setDeselectedEmployees((prev) => {
        const newSet = new Set(prev);
        // Selection is opt-out, so the clicked node's current checked state drives direction:
        // checked (absent from deselected) -> deselect the targets; unchecked -> reselect them.
        const isCurrentlySelected = !prev.has(login);
        targets.forEach((target) => (isCurrentlySelected ? newSet.add(target) : newSet.delete(target)));
        return newSet;
      });
    },
    [expandedNodes, employees]
  );

  const handleAddEmployee = useCallback((eaHuddleEmployee: Employee) => {
    const employee = convertEmployeeToOrgChartEmployee(eaHuddleEmployee);
    const employeeWithFlag = { ...employee, isManuallyAdded: true };

    setEmployees((prev) => new Map(prev).set(employee.login, employeeWithFlag));
    setManuallyAddedEmployees((prev) => new Map(prev).set(employee.login, employeeWithFlag));
    // Ensure a re-added employee starts selected (not carried over as deselected).
    setDeselectedEmployees((prev) => {
      const newSet = new Set(prev);
      newSet.delete(employee.login);
      return newSet;
    });
  }, []);

  const handleViewModeChange = useCallback((mode: 'direct' | 'all') => {
    setViewMode(mode);
  }, []);

  const handleLeaderChange = useCallback((eaHuddleEmployee: Employee) => {
    const newLeaderAlias = eaHuddleEmployee.login_name?.toLowerCase() || '';
    if (!newLeaderAlias) {
      setErrorMessage('Invalid employee selected. Please try again.');
      return;
    }

    setCurrentLeaderAlias(newLeaderAlias);
    setCurrentLeaderData(null);
    setEmployees(new Map());
    setExpandedNodes(new Set());
    setDeselectedEmployees(new Set());
    // manuallyAddedEmployees is intentionally NOT cleared so manual adds persist across leaders.
    setLoadingProgress(0);
    setTotalEmployees(0);
    setErrorMessage(null);
    setIsLoading(true);
  }, []);

  useEffect(() => {
    const abortController = new AbortController();
    const signal = abortController.signal;

    const loadInitialData = async () => {
      try {
        setIsLoading(true);
        setErrorMessage(null);
        setLoadingProgress(0);
        setTotalEmployees(0);

        let allAliases: string[] = [];

        if (viewMode === 'direct') {
          const directReportAliases = await fetchDirectReports(currentLeaderAlias, signal);
          allAliases = directReportAliases.filter((alias) => alias !== currentLeaderAlias);

          // Cap Direct Reports at MAX_EMPLOYEES (parity with All Reports). Abort any in-flight
          // request so we don't go on to load the per-employee batch for a selection we reject.
          if (allAliases.length > MAX_EMPLOYEES) {
            abortController.abort();
            setErrorMessage(MAX_EMPLOYEES_LIMIT_MESSAGE);
            setIsLoading(false);
            return;
          }
        } else {
          try {
            // Count first to fail fast if the org exceeds MAX_EMPLOYEES.
            const totalReportsCount = await withTimeout(
              countAllReportsUnderLeader(currentLeaderAlias, signal),
              API_TIMEOUT_MS,
              'API_TIMEOUT',
              signal
            );

            if (totalReportsCount > MAX_EMPLOYEES) {
              // Abort the in-flight count/fetch chain so we don't keep loading a rejected org.
              abortController.abort();
              setErrorMessage(MAX_EMPLOYEES_LIMIT_MESSAGE);
              setIsLoading(false);
              return;
            }

            const allReportAliases = await fetchAllReportsRecursively(currentLeaderAlias, signal);
            allAliases = allReportAliases.filter((alias) => alias !== currentLeaderAlias);
          } catch (error) {
            if (error instanceof Error && error.name === 'AbortError') return;

            if (error instanceof TimeoutError) {
              setErrorMessage(
                `Failed to load employees. This leader likely has a large number of reports and the request timed out. ` +
                  `Please use Direct Reports view instead.`
              );
            } else {
              setErrorMessage('Failed to load employee data. Please try again.');
            }
            setIsLoading(false);
            return;
          }
        }

        setTotalEmployees(allAliases.length + 1);

        const leaderResponse = await getEmployeeByLogin(currentLeaderAlias, 'job,direct-reports', signal);
        if (leaderResponse.employee) {
          setCurrentLeaderData(transformToOrgChartEmployee(leaderResponse.employee));
        }

        const aliasesToFetch = [currentLeaderAlias, ...allAliases];
        const fetchedEmployees = await fetchEmployeesBatchForOrgChart(aliasesToFetch, setLoadingProgress, signal);

        // Merge fetched employees with manually added ones not present in the fetched set.
        setEmployees(() => {
          const mergedEmployees = new Map(fetchedEmployees);
          manuallyAddedEmployees.forEach((employee, login) => {
            if (!mergedEmployees.has(login)) mergedEmployees.set(login, employee);
          });
          return mergedEmployees;
        });

        setExpandedNodes(new Set([currentLeaderAlias]));
        setIsLoading(false);

        logger.info('Initial data load completed', { totalEmployees: fetchedEmployees.size });
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') return;
        logger.error('Failed to load initial data');
        setErrorMessage('Failed to load employee data. Please try again.');
        setIsLoading(false);
      }
    };

    loadInitialData();

    return () => {
      abortController.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLeaderAlias, viewMode]);

  return {
    currentLeaderAlias,
    currentLeaderData,
    viewMode,
    employees,
    filteredEmployeesForDisplay,
    expandedNodes,
    selectedEmployees,
    selectedEmployeeCount,
    activeEmployeeCount,
    existingEmails,
    isLoading,
    loadingProgress,
    totalEmployees,
    errorMessage,
    setErrorMessage,
    handleExpand,
    handleCollapse,
    handleToggleSelection,
    handleAddEmployee,
    handleViewModeChange,
    handleLeaderChange
  };
};
